import { AlertCircle, ShieldX, WifiOff } from 'lucide-react'
import { AuthorizationError, ApiUnreachableError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface ErrorStateProps {
  error?: Error | null
  onRetry?: () => void
  /** Overrides the generic heading for an ordinary failure, e.g. "Couldn't load X". */
  title?: string
  /** Smaller footprint for use inside a dialog or card rather than a whole page. */
  compact?: boolean
}

export function ErrorState({ error, onRetry, title, compact }: ErrorStateProps) {
  const isAuthorizationError = error instanceof AuthorizationError
  const isUnreachable = error instanceof ApiUnreachableError
  const iconClassName = cn('text-danger', compact ? 'mb-3 h-8 w-8' : 'mb-4 h-12 w-12')
  const buttonSize = compact ? 'sm' : 'default'

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'py-8' : 'py-12'
      )}
    >
      {isUnreachable ? (
        <WifiOff className={iconClassName} />
      ) : isAuthorizationError ? (
        <ShieldX className={iconClassName} />
      ) : (
        <AlertCircle className={iconClassName} />
      )}
      <h3 className={cn('font-semibold text-text', compact ? 'mb-1 text-sm' : 'mb-2 text-lg')}>
        {isUnreachable
          ? "Can't reach the API"
          : isAuthorizationError
            ? 'Not authorized'
            : (title ?? 'Something went wrong')}
      </h3>
      <p className={cn('text-textMuted', compact ? 'mb-3 max-w-sm text-xs' : 'mb-4 text-sm')}>
        {error?.message || 'An unexpected error occurred'}
      </p>
      {isUnreachable ? (
        <div className="flex items-center gap-3">
          <Button asChild size={buttonSize}>
            <a href={error.baseUrl} target="_blank" rel="noopener noreferrer">
              Re-authenticate
            </a>
          </Button>
          {onRetry && (
            <Button variant="outline" size={buttonSize} onClick={onRetry}>
              Retry
            </Button>
          )}
        </div>
      ) : (
        // An ordinary failure (a 500, a validation error, ...) has no
        // re-authenticate step, but still deserves a way back when a caller
        // offers one - gating Retry on the error's own type left callers
        // whose `onRetry` would otherwise never render anything. Not for an
        // AuthorizationError, though: repeating the identical request cannot
        // fix a 401/403, so a Retry button there is a loop back to the same
        // failure, not a way out of it. Checked here rather than left to each
        // caller - `properties.tsx` already carried its own `isAuthorizationError
        // ? undefined : refetch` guard for exactly this, which existed only
        // because this component didn't enforce it itself.
        onRetry &&
        !isAuthorizationError && (
          <Button variant="outline" size={buttonSize} onClick={onRetry}>
            Retry
          </Button>
        )
      )}
    </div>
  )
}
